import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClientsAPI } from "../api/clients";
import type {
  ClientBranding,
  ClientCallRecording,
  ClientDeletionEligibility,
  ClientInvitation,
  ClientMemberWithUser,
  ClientRole,
  ClientWithMembership,
} from "../types";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useParams } from "react-router-dom";

// Query Keys
export const clientKeys = {
  all: ["clients"] as const,
  myClients: () => ["clients", "my"] as const,
  invitations: () => ["client_users", "invitations"] as const,
  /**
   * One key for the team **and** the accesses, because one request serves
   * both (`GET /clients/:id/team`). Attaching an access to a member changes
   * how each list reads, so they invalidate together.
   */
  team: (clientId: string) => ["client_users", "team", clientId] as const,
  deletionEligibility: (clientId: string) =>
    ["clients", "deletion-eligibility", clientId] as const,
};

/**
 * The companies to list: without the demo spaces, which nobody works in and
 * which are opened by their URL only. Only for lists and for picking a company to
 * open — roles and permissions read the whole of `useMyClients`, demo spaces included.
 */
export const withoutDemoSpaces = (
  clients: ClientWithMembership[],
): ClientWithMembership[] => clients.filter((c) => !c.demo_space);

/** The company to open by default: the first listed one, else the first at all. */
export const firstCompany = (
  clients: ClientWithMembership[],
): ClientWithMembership | undefined =>
  withoutDemoSpaces(clients)[0] || clients[0];

// Get My Clients Query
export function useMyClients() {
  const { isAuthenticated } = useAuth();
  return useQuery<ClientWithMembership[]>({
    queryKey: clientKeys.myClients(),
    queryFn: ClientsAPI.getMyClients,
    staleTime: 30 * 1000, // 30 seconds
    enabled: isAuthenticated,
  });
}

// Get Pending Invitations Query
export function usePendingInvitations() {
  return useQuery<ClientInvitation[]>({
    queryKey: clientKeys.invitations(),
    queryFn: ClientsAPI.getPendingInvitations,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * The team and the accesses, one request. The Équipe screen shows both; every
 * other caller wants the team, and uses {@link useClientMembers} below.
 */
export function useClientTeam(clientId: string) {
  return useQuery({
    queryKey: clientKeys.team(clientId),
    queryFn: () => ClientsAPI.getClientTeam(clientId),
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!clientId,
  });
}

/**
 * The team alone — who the agent can name, book and transfer to.
 *
 * Shares the query above rather than fetching again, so the pickers and the
 * Équipe screen cannot disagree about who is on the team.
 */
export function useClientMembers(clientId: string) {
  return useQuery({
    queryKey: clientKeys.team(clientId),
    queryFn: () => ClientsAPI.getClientTeam(clientId),
    select: (data): ClientMemberWithUser[] => data.members,
    staleTime: 30 * 1000, // 30 seconds
    enabled: !!clientId,
  });
}

/** Who can sign in to this tenant, and which team member each one is. */
export function useSetAccessMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      accessId,
      memberId,
    }: {
      clientId: string;
      accessId: string;
      memberId: string;
    }) => ClientsAPI.setAccessMember(clientId, accessId, memberId),
    onSuccess: (_result, { clientId }) => {
      queryClient.invalidateQueries({
        queryKey: clientKeys.team(clientId),
      });
    },
  });
}

/** Add somebody to the team without inviting them. */
export function useCreateMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      ...data
    }: {
      clientId: string;
      name: string;
      role_text?: string;
      phone?: string;
    }) => ClientsAPI.createMember(clientId, data),
    onSuccess: (_result, { clientId }) => {
      queryClient.invalidateQueries({
        queryKey: clientKeys.team(clientId),
      });
    },
  });
}

/** Remove somebody from the team. Their access, if any, is left alone. */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      memberId,
    }: {
      clientId: string;
      memberId: string;
    }) => ClientsAPI.removeMember(clientId, memberId),
    onSuccess: (_result, { clientId }) => {
      queryClient.invalidateQueries({
        queryKey: clientKeys.team(clientId),
      });
    },
  });
}

// Create Client Mutation
export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string }) => ClientsAPI.createClient(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.myClients() });
    },
  });
}

// Update Client Mutation
export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: {
        name?: string;
        logo?: string;
        website?: string;
        about?: string;
        address?: string;
        branding?: ClientBranding;
        call_recording?: ClientCallRecording;
      };
    }) => ClientsAPI.updateClient(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.myClients() });
    },
  });
}

// Company deletion eligibility (owner-only; the endpoint 403s for everyone else)
export function useClientDeletionEligibility(clientId: string, enabled = true) {
  return useQuery<ClientDeletionEligibility>({
    queryKey: clientKeys.deletionEligibility(clientId),
    queryFn: () => ClientsAPI.getDeletionEligibility(clientId),
    enabled: !!clientId && enabled,
    retry: false,
  });
}

// Delete Client Mutation
export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId: string) => ClientsAPI.deleteClient(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.myClients() });
    },
  });
}

// Invite User Mutation
export function useInviteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      email,
      role,
    }: {
      clientId: string;
      email: string;
      role?: ClientRole;
    }) => ClientsAPI.inviteUser(clientId, { email, role }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: clientKeys.team(variables.clientId),
      });
    },
  });
}

// Accept Invitation Mutation
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) =>
      ClientsAPI.acceptInvitation(invitationId),
    // Awaited: the caller navigates into the company it just joined, and the
    // company guards read the cached list. A stale one (without that company)
    // sends them to /start, which — the invitation still cached — sends them
    // back to /invitations, now empty. The invitations are refetched without
    // waiting, or the row would vanish into "no invitation" before we leave.
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.invitations() });
      await queryClient.invalidateQueries({
        queryKey: clientKeys.myClients(),
        refetchType: "all",
      });
    },
  });
}

/**
 * Take an access away: revoke a login, or cancel an invitation nobody
 * answered. One mutation, because it is one row — and keyed by the access id,
 * which is the only id a pending invitation has.
 */
export function useRemoveAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      accessId,
    }: {
      clientId: string;
      accessId: string;
    }) => ClientsAPI.removeAccess(clientId, accessId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: clientKeys.team(variables.clientId),
      });
    },
  });
}

/** Give up your own access to a client — the companies list, not a setting. */
export function useLeaveClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clientId: string) => ClientsAPI.leaveClient(clientId),
    onSuccess: (_, clientId) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.team(clientId) });
      queryClient.invalidateQueries({ queryKey: clientKeys.myClients() });
    },
  });
}

/** Change what an access may do in this client. */
export function useUpdateAccessRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      accessId,
      role,
    }: {
      clientId: string;
      accessId: string;
      role: ClientRole;
    }) => ClientsAPI.updateAccessRole(clientId, accessId, role),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: clientKeys.team(variables.clientId),
      });
    },
  });
}

/** Everything the agent knows about a team member. Keyed by member id. */
export function useUpdateMemberProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      clientId,
      memberId,
      updates,
    }: {
      clientId: string;
      memberId: string;
      updates: {
        display_name?: string;
        schedule_id?: string | null;
        calendars_ids?: { ids: string[] };
        phone?: string;
        device_id?: string;
        sip_extension?: string;
        accept_call_transfers?: boolean;
        role_text?: string;
        hidden_from_agents?: boolean;
      };
    }) => ClientsAPI.updateMemberProfile(clientId, memberId, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: clientKeys.team(variables.clientId),
      });
    },
  });
}

// Helper to get current client from URL
export function useCurrentClient() {
  const { data: clients } = useMyClients();
  const { company } = useParams<{ company: string }>();

  // This will be implemented when we have routing set up
  // For now, return the first client if available
  return clients?.find((a) => a.client.id === company) || clients?.[0] || null;
}
